import type { CampaignState, ClientState, FunnelGroup, GoalState, MailboxState, OpsAlert } from "./types";

export const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** A goal value in its unit — dollars, or a whole creator count. */
export const fmtUnit = (goal: GoalState, n: number) => (goal.unit === "usd" ? money.format(n) : String(Math.round(n)));

export const pct = (n: number | null) => (n === null ? "—" : `${Math.round(n * 100)}%`);

export const fmtAge = (hours: number | null) =>
  hours === null ? "—" : hours < 1 ? "<1 h" : hours < 48 ? `${Math.round(hours)} h` : `${Math.round(hours / 24)} d`;

export const GOAL_LABEL: Record<GoalState["kind"], string> = {
  commit_budget: "Committed + fees",
  waiting_approval_total: "Deals (plan counter)",
  activations_per_month: "Activations (plan counter)",
};

export type Tone = "success" | "warning" | "error" | "muted";

export const TONE_TEXT: Record<Tone, string> = {
  success: "text-success",
  warning: "text-warning",
  error: "text-destructive",
  muted: "text-muted-foreground",
};

/** Below 80% of the straight line to the target counts as behind — the controller's own threshold. */
const BEHIND_RATIO = 0.8;

export type Pace =
  | { kind: "no_target" }
  | { kind: "not_started" }
  | { kind: "on_pace" }
  | { kind: "behind"; short: number };

export function paceOf(goal: GoalState): Pace {
  if (!goal.target) return { kind: "no_target" };
  if (goal.expected_now === null) return { kind: "not_started" };
  if (goal.progress < goal.expected_now * BEHIND_RATIO) return { kind: "behind", short: goal.expected_now - goal.progress };
  return { kind: "on_pace" };
}

/** Client-level alert texts start with "<client name>: " — redundant next to the client's name. */
export function alertText(client: ClientState, alert: OpsAlert) {
  const prefix = `${client.name}: `;
  return alert.text.startsWith(prefix) ? alert.text.slice(prefix.length) : alert.text;
}

/** One status per client card, worst first. */
export function clientHealth(client: ClientState): { tone: Tone; label: string } {
  if (client.plan_error) return { tone: "error", label: "Plan invalid" };
  if (!client.org_id) return { tone: "muted", label: "Not mapped" };
  if (!client.org) return { tone: "error", label: "Org missing" };
  if (client.org.automation_paused) return { tone: "error", label: "Paused" };
  if (client.alerts.some((a) => a.level === "critical")) return { tone: "error", label: "Needs attention" };
  if (client.goal && paceOf(client.goal).kind === "behind") return { tone: "warning", label: "Behind pace" };
  const warns = client.alerts.filter((a) => a.level === "warn").length;
  if (warns > 0) return { tone: "warning", label: `${warns} warning${warns === 1 ? "" : "s"}` };
  return { tone: "success", label: "On track" };
}

const VERDICT_RANK: Record<MailboxState["verdict"], number> = { weak: 3, ok: 2, immature: 1, idle: 0 };

/** A client's mailboxes across its running campaigns, one per account (worst verdict wins). */
export function clientMailboxes(campaigns: CampaignState[]): MailboxState[] {
  const byAccount = new Map<string, MailboxState>();
  for (const c of campaigns.filter((c) => c.running)) {
    for (const m of c.mailboxes) {
      const seen = byAccount.get(m.account_id);
      if (!seen || VERDICT_RANK[m.verdict] > VERDICT_RANK[seen.verdict]) byAccount.set(m.account_id, m);
    }
  }
  return [...byAccount.values()];
}

export const VERDICT: Record<MailboxState["verdict"], { label: string; dot: string; hint: string }> = {
  ok: { label: "ok", dot: "bg-success", hint: "Matured reply rate is healthy" },
  weak: { label: "weak", dot: "bg-destructive", hint: "Matured reply rate under the plan's floor, or far below its sibling mailboxes" },
  immature: { label: "too early", dot: "bg-info/60", hint: "Not judged yet: needs the plan's minimum days and matured sends" },
  idle: { label: "idle", dot: "bg-muted-foreground/30", hint: "Has not contacted anyone in this campaign" },
};

/** Funnel segments in pipeline order; post-approval and completed read as one "approved" step. */
export const FUNNEL_STEPS: { key: string; label: string; color: string; groups: FunnelGroup[] }[] = [
  { key: "pending", label: "Not launched", color: "bg-muted-foreground/20", groups: ["pending"] },
  { key: "queued", label: "Queued", color: "bg-muted-foreground/45", groups: ["queued"] },
  { key: "outreached", label: "Outreached", color: "bg-info/45", groups: ["outreached"] },
  { key: "negotiating", label: "Negotiating", color: "bg-info", groups: ["negotiating"] },
  { key: "waiting_approval", label: "Waiting approval", color: "bg-warning", groups: ["waiting_approval"] },
  { key: "approved", label: "Approved", color: "bg-success", groups: ["post_approval", "completed"] },
  { key: "declined", label: "Declined", color: "bg-destructive/45", groups: ["declined"] },
  { key: "rejected", label: "Rejected", color: "bg-destructive/80", groups: ["rejected"] },
  { key: "other", label: "Other", color: "bg-muted-foreground/10", groups: ["other"] },
];

/** Totals a client's running campaigns into the four numbers its card shows. */
export function supplyOf(client: ClientState) {
  const running = client.campaigns.filter((c) => c.running);
  const runways = running.map((c) => c.runway_days).filter((d): d is number => d !== null);
  return {
    running,
    queue: running.reduce((s, c) => s + c.to_contact, 0),
    // A queue no mailbox can send from has no runway at all.
    blocked: running.some((c) => c.runway_days === null && c.to_contact > 0),
    runway: runways.length ? Math.min(...runways) : null,
    followups: running.reduce((s, c) => s + (c.first_followups_owed ?? 0), 0),
  };
}
